/* OCR/Manuel fatura ekleme modali (accounting modulunde tab olarak)
   Accounting.js'den ayrıştırıldı (bakım kolaylığı için).
   - 2 paralel upload slot + paralel polling
   - Genel ilerleme barı + ETA
   - Her dosya için faz takibi (queued/uploading/extracting)
*/
import React, { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Sparkles, X, RefreshCw, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtMoney = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const MAX_OCR_FILES = 50;
const OCR_PARALLEL = 4;        // 4 worker (polling paralel; upload ayrica 2 slot ile sinirli)
const OCR_UPLOAD_SLOTS = 2;    // backend'e ayni anda max 2 upload (cloudflare/ingress safe)
const OCR_TIMEOUT_MS = 600000; // 10 dakika
const OCR_ALLOWED_MIMES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const OCR_ALLOWED_EXT_RE = /\.(pdf|jpg|jpeg|png|webp|heic|heif)$/i;

export function OcrUploadModal({ onClose, onSaved }) {
    // files[i] = { id, file, previewUrl, status, extracted, error, editing }
    //   status: 'pending' | 'processing' | 'done' | 'error' | 'saving' | 'saved' | 'skipped'
    const [mode, setMode] = useState('ocr');   // 'ocr' | 'manual'
    const [files, setFiles] = useState([]);
    const [hintType, setHintType] = useState('purchase');
    const [dragOver, setDragOver] = useState(false);
    const [batchRunning, setBatchRunning] = useState(false);
    const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });
    const [bulkSaving, setBulkSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [minimized, setMinimized] = useState(false);

    const addFiles = (fileList) => {
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;
        const accepted = [];
        const rejected = [];
        for (const f of incoming) {
            if (f.size > 20 * 1024 * 1024) { rejected.push(`${f.name}: 20MB ustu`); continue; }
            if (!OCR_ALLOWED_MIMES.includes(f.type) && !OCR_ALLOWED_EXT_RE.test(f.name)) {
                rejected.push(`${f.name}: format desteksiz`); continue;
            }
            accepted.push(f);
        }
        setFiles(prev => {
            const room = MAX_OCR_FILES - prev.length;
            const slice = accepted.slice(0, Math.max(0, room));
            if (accepted.length > room) {
                rejected.push(`${accepted.length - room} dosya eklenmedi (max ${MAX_OCR_FILES})`);
            }
            const mapped = slice.map((f, idx) => ({
                id: `f-${Date.now()}-${prev.length + idx}-${Math.random().toString(36).slice(2, 7)}`,
                file: f,
                previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
                status: 'pending',
                extracted: null,
                error: null,
            }));
            return [...prev, ...mapped];
        });
        if (rejected.length) toast.error(rejected.join(', '), { duration: 5000 });
    };

    const updateFile = (id, patch) => setFiles(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));

    // ── OTOMATIK TARAMA ──
    // Yeni eklenenler arka planda taranmaya baslar. Upload semaphore aktif
    // oldugu icin burada slots=4 paralel olabilir; upload yine 2 slot ile sinirli.
    const autoScanInFlight = React.useRef(new Set());
    React.useEffect(() => {
        const pendings = files.filter(f => f.status === 'pending' && !autoScanInFlight.current.has(f.id));
        if (!pendings.length) return;
        const inFlight = files.filter(f => f.status === 'processing').length;
        const slots = Math.max(0, 4 - inFlight);
        if (slots <= 0) return;
        for (const f of pendings.slice(0, slots)) {
            autoScanInFlight.current.add(f.id);
            runOcrOne(f).finally(() => autoScanInFlight.current.delete(f.id));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files.length, files.map(f => f.status).join(',')]);

    const removeFile = (id) => setFiles(prev => {
        const tgt = prev.find(f => f.id === id);
        if (tgt?.previewUrl) URL.revokeObjectURL(tgt.previewUrl);
        return prev.filter(f => f.id !== id);
    });

    // Upload semaphore: max OCR_UPLOAD_SLOTS upload backend'e ayni anda gider.
    // Polling'ler paralel devam eder (hafif istekler). 2 slot = browser HTTP
    // limitine ve cloudflare ingress'ine takilmadan ~2x hizlanma.
    const uploadSlots = React.useRef({
        chains: Array(OCR_UPLOAD_SLOTS).fill(null).map(() => Promise.resolve()),
        next: 0,
    });
    const acquireUploadSlot = (fn) => {
        const idx = uploadSlots.current.next;
        uploadSlots.current.next = (idx + 1) % OCR_UPLOAD_SLOTS;
        const turn = uploadSlots.current.chains[idx].then(() => fn());
        uploadSlots.current.chains[idx] = turn.catch(() => null);
        return turn;
    };

    // Tamamlanan dosyalarin gecen surelerini topla -> kalan sure (ETA) tahmini
    const completedDurations = React.useRef([]);   // ms cinsinden

    const runOcrOne = async (f) => {
        const t0 = Date.now();
        updateFile(f.id, { status: 'processing', error: null, _upload_phase: 'queued', _t0: t0 });

        // 1) UPLOAD asamasi - 2 slotlu semaphore
        let jobId = null;
        const myUploadTurn = acquireUploadSlot(async () => {
            updateFile(f.id, { status: 'processing', _upload_phase: 'uploading' });
            const fd = new FormData();
            fd.append('file', f.file);
            fd.append('hint_type', hintType);
            const startResp = await axios.post(
                `${API}/api/accounting/invoices/ocr-extract-async`, fd,
                {
                    headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
                    timeout: 120000,
                }
            );
            return startResp.data.job_id;
        });

        try {
            jobId = await myUploadTurn;
            if (!jobId) throw new Error('Sunucu job_id donmedi');

            // 2) POLLING asamasi - paralel
            updateFile(f.id, { status: 'processing', _upload_phase: 'extracting' });
            const pollStart = Date.now();
            const POLL_INTERVAL = 3000;
            while (Date.now() - pollStart < OCR_TIMEOUT_MS) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise(r => setTimeout(r, POLL_INTERVAL));
                // eslint-disable-next-line no-await-in-loop
                const sr = await axios.get(
                    `${API}/api/accounting/invoices/ocr-extract-job/${jobId}`,
                    { headers: headers(), timeout: 15000 }
                );
                const s = sr.data;
                if (s.status === 'done') {
                    completedDurations.current.push(Date.now() - t0);
                    if (completedDurations.current.length > 20) completedDurations.current.shift();
                    updateFile(f.id, { status: 'done', extracted: s.extracted, warnings: s.warnings || [], _upload_phase: null });
                    return true;
                }
                if (s.status === 'error') {
                    updateFile(f.id, { status: 'error', error: s.error || 'OCR hatasi', _upload_phase: null });
                    return false;
                }
                const elapsedSec = s.elapsed_sec || 0;
                updateFile(f.id, {
                    status: 'processing',
                    _upload_phase: 'extracting',
                    _elapsed: elapsedSec,
                    _queued: s.status === 'queued',
                    _slow: elapsedSec > 60,
                });
            }
            updateFile(f.id, { status: 'error', error: `Tarama 10dk'yi asti - tekrar dene`, _upload_phase: null });
            return false;
        } catch (e) {
            let msg;
            const status = e.response?.status;
            if (e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '')) {
                msg = 'Yukleme yavas - Tekrar dene';
            } else if (e.code === 'ERR_NETWORK' || /network/i.test(e.message || '')) {
                msg = 'Ag yogun - Hatalilari Tekrar Tara ile yeniden dene';
            } else if (status === 502 || status === 503 || status === 504) {
                msg = `Sunucu yogun (${status}) - Hatalilari Tekrar Tara`;
            } else {
                const det = e.response?.data?.detail;
                msg = typeof det === 'string' ? det
                    : det?.message || `OCR hatasi (${e.response?.status || 'ag'})`;
            }
            updateFile(f.id, { status: 'error', error: msg, _upload_phase: null });
            return false;
        }
    };

    const runAllOcr = async () => {
        const pending = files.filter(f => f.status === 'pending' || f.status === 'error');
        if (!pending.length) { toast('Tarama gereken dosya yok'); return; }
        setBatchRunning(true);
        setBatchProgress({ done: 0, total: pending.length });
        let successCount = 0;
        let failCount = 0;
        const queue = [...pending];
        const workers = Array.from({ length: Math.min(OCR_PARALLEL, queue.length) }, async () => {
            while (queue.length) {
                const next = queue.shift();
                if (!next) break;
                // eslint-disable-next-line no-await-in-loop
                const ok = await runOcrOne(next);
                if (ok) successCount += 1; else failCount += 1;
                setBatchProgress(p => ({ ...p, done: p.done + 1 }));
            }
        });
        await Promise.all(workers);
        setBatchRunning(false);
        setBatchProgress({ done: 0, total: 0 });
        if (failCount === 0) {
            toast.success(`Tarama tamam: ${successCount} dosya islendi`);
        } else {
            toast.warning(`${successCount} basarili, ${failCount} hatali - hatali olanlara tekrar "Tara"ya basabilirsin`, { duration: 7000 });
        }
    };

    const saveOne = async (f, opts = {}) => {
        if (!f.extracted) return false;
        const ext = f.extracted;
        const payload = {
            type: ext.type || 'purchase',
            invoice_number: ext.invoice_number || '',
            invoice_date: ext.invoice_date || new Date().toISOString().slice(0, 10),
            due_date: ext.due_date || '',
            contact_name: ext.contact_name || '',
            vat_number: ext.vat_number || '',
            iban: ext.iban || '',
            currency: ext.currency || 'EUR',
            subtotal: ext.subtotal || 0,
            vat_amount: ext.vat_amount || 0,
            total: ext.total || 0,
            payment_reference: ext.payment_reference || '',
            notes: `OCR (${ext._ocr?.filename || f.file.name})`,
            force_duplicate: opts.force_duplicate || false,
            skip_recon: opts.skip_recon || false,
        };
        if (!payload.invoice_number || payload.total <= 0) {
            updateFile(f.id, { status: 'error', error: 'Fatura no veya tutar bos - duzenle' });
            return false;
        }
        updateFile(f.id, { status: 'saving' });
        try {
            const r = await axios.post(`${API}/api/accounting/invoices`, payload, {
                headers: headers(),
                timeout: 30000,
            });
            const rc = r.data?._recon || {};
            const info = [];
            if (r.data?._auto_company_created) info.push('firma+');
            if (rc.auto_matched > 0) info.push(`bk:${rc.auto_matched}`);
            else if (rc.pending_suggestions > 0) info.push(`oneri:${rc.pending_suggestions}`);
            if (opts.force_duplicate) info.push('duplicate');
            updateFile(f.id, { status: 'saved', saved_info: info.join(' ') });
            // Kaydedilenleri 2 saniye sonra listeden temizle ki kullanici "✓" tikini gorsun
            setTimeout(() => {
                setFiles(prev => prev.filter(x => x.id !== f.id));
            }, 2000);
            return true;
        } catch (e) {
            const status = e.response?.status;
            const detail = e.response?.data?.detail;
            // Duplicate fatura → bulk save'de autoForceDuplicate ile yeniden dene
            if (status === 409 && detail?.error === 'duplicate_invoice') {
                if (opts.autoForceDuplicate) {
                    return saveOne(f, { ...opts, force_duplicate: true, autoForceDuplicate: false });
                }
                updateFile(f.id, { status: 'done', error: `Duplicate: ${detail.message || 'zaten var'}` });
                return false;
            }
            let msg;
            if (Array.isArray(detail)) {
                msg = detail.map(d => d?.msg || d?.message || JSON.stringify(d)).slice(0, 2).join('; ');
            } else if (typeof detail === 'string') {
                msg = detail;
            } else if (detail?.message) {
                msg = detail.message;
            } else if (e.code === 'ERR_NETWORK' || /network/i.test(e.message || '')) {
                msg = 'Sunucuya baglanilamadi';
            } else if (status === 504 || status === 502) {
                msg = `Sunucu yanit vermedi (${status})`;
            } else if (status === 500) {
                msg = 'Sunucu hatasi - destege ulasin';
            } else if (e.message) {
                msg = `${e.message}${status ? ` (HTTP ${status})` : ''}`;
            } else {
                msg = `Kayit hatasi (HTTP ${status || '?'})`;
            }
            updateFile(f.id, { status: 'error', error: msg });
            return false;
        }
    };

    const saveAllDone = async () => {
        const ready = files.filter(f => f.status === 'done' && f.extracted?.invoice_number && f.extracted?.total > 0);
        if (!ready.length) { toast.error('Kaydedilecek hazir fatura yok'); return; }
        const autoForce = window.confirm(
            `${ready.length} fatura kaydedilecek.\n\n` +
            `Tamam = Kaydet (duplicate olanlari OTOMATIK yeniden kaydet)\n` +
            `Iptal = Iptal et`
        );
        if (!autoForce) return;
        setBulkSaving(true);
        let success = 0;
        let dupSkipped = 0;
        let failed = 0;
        const BULK_PARALLEL = 5;
        const queue = [...ready];
        const workers = Array.from(
            { length: Math.min(BULK_PARALLEL, queue.length) },
            async () => {
                while (queue.length) {
                    const next = queue.shift();
                    if (!next) break;
                    // eslint-disable-next-line no-await-in-loop
                    const ok = await saveOne(next, { autoForceDuplicate: true, skip_recon: true });
                    if (ok) success += 1;
                    else if (next.status === 'done' && /Duplicate/i.test(next.error || '')) dupSkipped += 1;
                    else failed += 1;
                }
            }
        );
        await Promise.all(workers);
        // Bulk save sonunda 1 kere tum recon'u tetikle
        try {
            await axios.post(`${API}/api/accounting/reconciliation/auto`, {}, { headers: headers() });
        } catch (_e) { /* sessizce gec */ }
        setBulkSaving(false);
        const parts = [`${success}/${ready.length} kaydedildi`];
        if (dupSkipped) parts.push(`${dupSkipped} duplicate`);
        if (failed) parts.push(`${failed} hata`);
        toast.success(parts.join(' · '));
        if (success > 0) onSaved?.();
    };

    const handleEditSave = (id, updated) => {
        updateFile(id, { extracted: updated, status: 'done', error: null });
        setEditingId(null);
    };

    const statusBadge = (s) => {
        const map = {
            pending: ['bg-gray-100 text-gray-600', 'Bekliyor'],
            processing: ['bg-blue-100 text-blue-700 animate-pulse', 'Tarayor...'],
            done: ['bg-emerald-50 text-emerald-700', 'Hazir'],
            error: ['bg-red-50 text-red-600', 'Hata'],
            saving: ['bg-purple-100 text-purple-700 animate-pulse', 'Kaydediyor'],
            saved: ['bg-emerald-600 text-white', 'Kaydedildi'],
            skipped: ['bg-gray-200 text-gray-500', 'Atlandi'],
        };
        const [cls, lbl] = map[s] || ['bg-gray-100', s];
        return <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${cls}`}>{lbl}</span>;
    };

    // Detayli faz etiketi (processing icinde alt-durum)
    const phaseLabel = (f) => {
        if (f.status !== 'processing') return null;
        const ph = f._upload_phase;
        if (ph === 'queued') return { txt: 'Sirada bekliyor', cls: 'text-gray-500' };
        if (ph === 'uploading') return { txt: 'Yukleniyor...', cls: 'text-amber-600' };
        if (ph === 'extracting') {
            if (f._queued) return { txt: 'AI sirasinda...', cls: 'text-blue-600' };
            if (f._slow) return { txt: `AI okuyor (${f._elapsed}s · yogun)`, cls: 'text-amber-700' };
            return { txt: f._elapsed ? `AI okuyor (${f._elapsed}s)` : 'AI okuyor...', cls: 'text-purple-700' };
        }
        return { txt: 'Isleniyor...', cls: 'text-blue-600' };
    };

    const editingFile = files.find(f => f.id === editingId);
    const doneCount = files.filter(f => f.status === 'done').length;
    const savedCount = files.filter(f => f.status === 'saved').length;
    const errorCount = files.filter(f => f.status === 'error').length;
    const processingCount = files.filter(f => f.status === 'processing' || f.status === 'saving').length;
    const pendingCount = files.filter(f => f.status === 'pending').length;

    // Genel ilerleme: islenmis (done + saved + error) / toplam
    const processedCount = doneCount + savedCount + errorCount;
    const totalForProgress = files.length;
    const progressPct = totalForProgress > 0 ? Math.round((processedCount / totalForProgress) * 100) : 0;

    // ETA: ortalama dosya basina sure × kalan dosya / paralel slot
    const avgMs = completedDurations.current.length > 0
        ? completedDurations.current.reduce((a, b) => a + b, 0) / completedDurations.current.length
        : 0;
    const remaining = pendingCount + processingCount;
    const etaSec = avgMs > 0 ? Math.ceil((avgMs * remaining) / OCR_UPLOAD_SLOTS / 1000) : 0;
    const etaLabel = etaSec > 0
        ? (etaSec >= 60 ? `~${Math.ceil(etaSec / 60)} dk kaldi` : `~${etaSec} sn kaldi`)
        : '';

    return (
        <>
            {/* MINIMIZED: alt-sag kose floating widget */}
            {minimized && (
                <div className="fixed bottom-6 right-6 z-50 bg-white rounded-2xl shadow-2xl border border-purple-300 px-4 py-3 flex items-center gap-3 cursor-pointer hover:scale-105 transition-transform"
                    onClick={() => setMinimized(false)}
                    data-testid="acc-ocr-minimized">
                    <div className="flex items-center gap-2">
                        {(processingCount > 0 || batchRunning) ? (
                            <RefreshCw size={16} className="animate-spin text-purple-600" />
                        ) : (
                            <Sparkles size={16} className="text-purple-600" />
                        )}
                        <div>
                            <div className="text-xs font-bold text-[#111]">OCR Tarama</div>
                            <div className="text-[10px] text-[#6B7280]">
                                {batchRunning
                                    ? `Tariyor ${batchProgress.done}/${batchProgress.total}...`
                                    : processingCount > 0
                                    ? `${processingCount} dosya isleniyor`
                                    : `${files.length} dosya | ${doneCount} hazir`}
                                {errorCount > 0 && <span className="text-red-500 ml-1">· {errorCount} hata</span>}
                            </div>
                        </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setMinimized(false); }}
                        className="ml-2 h-7 px-2 bg-purple-600 text-white text-[10px] font-bold rounded-md"
                        data-testid="acc-ocr-restore-btn">
                        AC
                    </button>
                </div>
            )}

            {!minimized && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setMinimized(true)}>
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" data-testid="acc-ocr-modal">
                <div className="px-5 py-3 border-b border-[#F3F4F6] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex bg-[#F3F4F6] rounded-lg p-0.5">
                            <button onClick={() => setMode('ocr')}
                                className={`px-3 py-1 rounded-md text-xs font-semibold flex items-center gap-1 ${mode === 'ocr' ? 'bg-white shadow-sm text-purple-700' : 'text-[#6B7280]'}`}
                                data-testid="acc-modal-tab-ocr">
                                <Sparkles size={11} />OCR ile Otomatik
                            </button>
                            <button onClick={() => setMode('manual')}
                                className={`px-3 py-1 rounded-md text-xs font-semibold ${mode === 'manual' ? 'bg-white shadow-sm text-[#111]' : 'text-[#6B7280]'}`}
                                data-testid="acc-modal-tab-manual">
                                Manuel Giris
                            </button>
                        </div>
                        {mode === 'ocr' && <span className="text-[10px] text-[#9CA3AF]">{files.length}/{MAX_OCR_FILES} dosya</span>}
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={() => setMinimized(true)}
                            className="h-7 px-2 text-[#6B7280] hover:bg-[#F3F4F6] rounded text-xs font-semibold flex items-center gap-1"
                            data-testid="acc-ocr-minimize-btn"
                            title="Modal'i kucult - OCR arka planda devam eder, sistemi rahatca kullanabilirsin">
                            <span className="text-base leading-none">_</span>
                            <span className="hidden sm:inline">Kucult</span>
                        </button>
                        <button onClick={onClose} className="h-7 w-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#111] hover:bg-[#F3F4F6] rounded" data-testid="acc-ocr-close"><X size={16} /></button>
                    </div>
                </div>

                {mode === 'manual' ? (
                    <div className="overflow-y-auto p-1">
                        <InvoiceFormInline onSaved={() => { onSaved?.(); onClose(); }} />
                    </div>
                ) : (
                    <>
                <div className="px-5 py-3 border-b border-[#F3F4F6] flex items-center gap-3 flex-wrap bg-[#FAFAFA]">
                    <div className="flex items-center gap-1.5">
                        <label className="text-[10px] font-semibold text-[#6B7280]">Belge tipi:</label>
                        <select value={hintType} onChange={e => setHintType(e.target.value)}
                            className="h-7 px-2 bg-white border border-[#E5E7EB] rounded-md text-xs" data-testid="acc-ocr-hint-type">
                            <option value="purchase">Alis Faturasi</option>
                            <option value="sales">Satis Faturasi</option>
                            <option value="purchase_credit">Alis Iade</option>
                            <option value="sales_credit">Satis Iade</option>
                            <option value="receipt">Fis (Receipt)</option>
                        </select>
                    </div>
                    <div className="flex-1"></div>
                    {files.length > 0 && (
                        <>
                            <div className="text-[10px] text-[#6B7280] flex items-center gap-2">
                                {processingCount > 0 && <span className="text-blue-600 font-semibold">{processingCount} isleniyor</span>}
                                {doneCount > 0 && <span className="text-emerald-600 font-semibold">{doneCount} hazir</span>}
                                {errorCount > 0 && <span className="text-red-600 font-semibold">{errorCount} hata</span>}
                                {savedCount > 0 && <span className="text-emerald-700 font-bold">{savedCount} kaydedildi</span>}
                            </div>
                            <button onClick={runAllOcr} disabled={batchRunning || files.every(f => f.status !== 'pending' && f.status !== 'error')}
                                className="h-8 px-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-semibold rounded-lg flex items-center gap-1 disabled:opacity-50"
                                data-testid="acc-ocr-run-all-btn">
                                {batchRunning ? <><RefreshCw size={11} className="animate-spin" />Tariyor {batchProgress.done}/{batchProgress.total}...</> : <><Sparkles size={11} />Hepsini Tara</>}
                            </button>
                            {errorCount > 0 && !batchRunning && (
                                <button onClick={() => {
                                    setFiles(prev => prev.map(f => f.status === 'error' ? { ...f, status: 'pending', error: null } : f));
                                    setTimeout(() => runAllOcr(), 100);
                                }}
                                    className="h-8 px-3 bg-amber-100 text-amber-700 text-xs font-semibold rounded-lg flex items-center gap-1 hover:bg-amber-200"
                                    data-testid="acc-ocr-retry-errors-btn">
                                    <RefreshCw size={11} />Hatalilari Tekrar Tara ({errorCount})
                                </button>
                            )}
                            <button onClick={saveAllDone} disabled={bulkSaving || doneCount === 0}
                                className="h-8 px-3 bg-[#111827] text-white text-xs font-semibold rounded-lg flex items-center gap-1 disabled:opacity-50"
                                data-testid="acc-ocr-save-all-btn">
                                {bulkSaving ? <><RefreshCw size={11} className="animate-spin" />Kaydediyor...</> : <><CheckCircle2 size={11} />Hazirlari Kaydet ({doneCount})</>}
                            </button>
                        </>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    <label
                        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                        className={`block border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition ${dragOver ? 'border-purple-500 bg-purple-50' : 'border-[#E5E7EB] hover:border-purple-300 hover:bg-purple-50/30'} ${files.length >= MAX_OCR_FILES ? 'opacity-40 pointer-events-none' : ''}`}
                        data-testid="acc-ocr-drop-zone">
                        <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,application/pdf,image/*"
                            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} className="hidden"
                            data-testid="acc-ocr-file-input" />
                        <div className="flex items-center justify-center gap-2">
                            <Sparkles size={14} className="text-purple-600" />
                            <p className="text-xs font-semibold text-[#111]">
                                {files.length === 0 ? `Buraya surukle veya tikla - max ${MAX_OCR_FILES} dosya` : `+ Yeni ekle (${MAX_OCR_FILES - files.length} kalan)`}
                            </p>
                        </div>
                        <p className="text-[10px] text-[#9CA3AF] mt-0.5">PDF / PNG / JPG / WEBP · Max 20MB / dosya · 2 paralel upload</p>
                    </label>

                    {/* GENEL ILERLEME BARI - sticky */}
                    {files.length > 0 && (
                        <div className="sticky top-0 z-10 bg-white border border-[#E5E7EB] rounded-xl p-3 shadow-sm" data-testid="acc-ocr-progress-bar">
                            <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2 text-[11px] font-semibold text-[#111]">
                                    <span data-testid="acc-ocr-progress-label">{processedCount}/{totalForProgress} islendi</span>
                                    {processingCount > 0 && (
                                        <span className="text-blue-600 flex items-center gap-1">
                                            <RefreshCw size={10} className="animate-spin" />
                                            {processingCount} isleniyor
                                        </span>
                                    )}
                                    {errorCount > 0 && (
                                        <span className="text-red-600">· {errorCount} hata</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 text-[10px] text-[#6B7280]">
                                    {etaLabel && remaining > 0 && <span data-testid="acc-ocr-eta">{etaLabel}</span>}
                                    <span className="font-bold text-[#111]">%{progressPct}</span>
                                </div>
                            </div>
                            <div className="w-full h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                                <div
                                    className={`h-full transition-all duration-300 ${errorCount > 0 ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-red-400' : 'bg-gradient-to-r from-purple-500 to-pink-500'}`}
                                    style={{ width: `${progressPct}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {files.length === 0 && (
                        <div className="text-center py-8 text-[11px] text-[#9CA3AF]" data-testid="acc-ocr-empty">
                            Birden fazla fatura/fis ekleyip aynı anda taratabilirsin
                        </div>
                    )}

                    {files.map(f => {
                        const ext = f.extracted;
                        const fileWarn = !ext?.invoice_number || (ext?.total || 0) <= 0;
                        return (
                            <div key={f.id} className={`border rounded-xl p-3 ${f.status === 'saved' ? 'border-emerald-300 bg-emerald-50/50' : f.status === 'error' ? 'border-red-200 bg-red-50/40' : 'border-[#E5E7EB] bg-white'}`}
                                data-testid={`acc-ocr-row-${f.id}`}>
                                <div className="flex items-start gap-3">
                                    {f.previewUrl ? (
                                        <img src={f.previewUrl} alt="" className="w-14 h-14 object-cover rounded-md flex-shrink-0 border border-[#E5E7EB]" />
                                    ) : (
                                        <div className="w-14 h-14 bg-purple-50 rounded-md flex items-center justify-center flex-shrink-0 text-purple-600 text-[9px] font-bold">PDF</div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                            <span className="text-xs font-semibold text-[#111] truncate max-w-[280px]" title={f.file.name}>{f.file.name}</span>
                                            {statusBadge(f.status)}
                                            <span className="text-[10px] text-[#9CA3AF]">{(f.file.size / 1024).toFixed(0)} KB</span>
                                        </div>
                                        {ext && (f.status === 'done' || f.status === 'saving' || f.status === 'saved') && (
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-0.5 text-[11px]">
                                                <div><span className="text-[#9CA3AF]">No:</span> <strong className={!ext.invoice_number ? 'text-red-500' : 'text-[#111]'}>{ext.invoice_number || '—'}</strong></div>
                                                <div><span className="text-[#9CA3AF]">Tarih:</span> <strong>{ext.invoice_date || '—'}</strong></div>
                                                <div><span className="text-[#9CA3AF]">Firma:</span> <strong className="text-[#111] truncate inline-block max-w-[140px] align-bottom">{ext.contact_name || '—'}</strong></div>
                                                <div><span className="text-[#9CA3AF]">Tutar:</span> <strong className={!ext.total ? 'text-red-500' : 'text-emerald-600'}>{fmtMoney(ext.total)} {ext.currency}</strong></div>
                                            </div>
                                        )}
                                        {f.status === 'saved' && f.saved_info && (
                                            <p className="text-[10px] text-emerald-700 mt-1 font-semibold" data-testid={`acc-ocr-saved-info-${f.id}`}>✓ {f.saved_info}</p>
                                        )}
                                        {f.error && <p className="text-[10px] text-red-600 mt-1">{f.error}</p>}
                                        {fileWarn && f.status === 'done' && <p className="text-[10px] text-amber-600 mt-0.5">⚠ Fatura no/tutar eksik - "Duzenle" ile tamamla</p>}
                                    </div>
                                    <div className="flex flex-col gap-1 flex-shrink-0">
                                        {f.status === 'pending' && (
                                            <button onClick={() => runOcrOne(f)} className="h-7 px-2 bg-purple-600 text-white text-[10px] font-semibold rounded-md" data-testid={`acc-ocr-run-${f.id}`}>Tara</button>
                                        )}
                                        {f.status === 'processing' && (() => {
                                            const ph = phaseLabel(f);
                                            return (
                                                <span className={`text-[10px] font-semibold flex items-center gap-1 ${ph?.cls || 'text-purple-700'}`} data-testid={`acc-ocr-processing-${f.id}`}>
                                                    <RefreshCw size={10} className="animate-spin" />
                                                    {ph?.txt || 'Isleniyor...'}
                                                </span>
                                            );
                                        })()}
                                        {f.status === 'saving' && (
                                            <span className="text-[10px] text-emerald-700 font-semibold flex items-center gap-1">
                                                <RefreshCw size={10} className="animate-spin" />Kaydediliyor...
                                            </span>
                                        )}
                                        {f.status === 'error' && (
                                            <button onClick={() => runOcrOne(f)} className="h-7 px-2 bg-purple-600 text-white text-[10px] font-semibold rounded-md">Tekrar</button>
                                        )}
                                        {f.status === 'done' && (
                                            <>
                                                <button onClick={() => setEditingId(f.id)} className="h-6 px-2 bg-white border border-[#E5E7EB] text-[#111] text-[10px] font-semibold rounded-md" data-testid={`acc-ocr-edit-${f.id}`}>Duzenle</button>
                                                <button onClick={() => saveOne(f)} disabled={fileWarn} className="h-6 px-2 bg-emerald-600 text-white text-[10px] font-semibold rounded-md disabled:opacity-40" data-testid={`acc-ocr-save-${f.id}`}>Kaydet</button>
                                            </>
                                        )}
                                        {f.status !== 'saved' && (
                                            <button onClick={() => removeFile(f.id)} className="h-6 px-2 bg-white border border-red-200 text-red-500 text-[10px] font-semibold rounded-md" data-testid={`acc-ocr-remove-${f.id}`}>Sil</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                    </>
                )}
            </div>

            {editingFile && (
                <BatchOcrEditModal
                    file={editingFile}
                    onClose={() => setEditingId(null)}
                    onSave={(updated) => handleEditSave(editingFile.id, updated)}
                />
            )}
        </div>
            )}
        </>
    );
}

// Inline manuel fatura formu (modal icinde tab olarak)
function InvoiceFormInline({ onSaved }) {
    const [form, setForm] = useState({
        type: 'purchase',
        invoice_number: '',
        invoice_date: new Date().toISOString().slice(0, 10),
        due_date: '',
        contact_name: '',
        vat_number: '',
        iban: '',
        currency: 'EUR',
        subtotal: 0,
        vat_amount: 0,
        total: 0,
        payment_reference: '',
        notes: '',
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
    const submit = async (e) => {
        e.preventDefault();
        if (!form.invoice_number || (parseFloat(form.total) || 0) <= 0) {
            toast.error('Fatura no ve tutar gerekli');
            return;
        }
        setSaving(true);
        try {
            const r = await axios.post(`${API}/api/accounting/invoices`, {
                ...form,
                subtotal: parseFloat(form.subtotal) || 0,
                vat_amount: parseFloat(form.vat_amount) || 0,
                total: parseFloat(form.total) || 0,
            }, { headers: headers() });
            const extras = [];
            if (r.data?._auto_company_created) extras.push('firma olusturuldu');
            const rc = r.data?._recon;
            if (rc?.auto_matched > 0) extras.push(`${rc.auto_matched} banka transferi eslesti`);
            else if (rc?.pending_suggestions > 0) extras.push(`${rc.pending_suggestions} oneri olusturuldu`);
            toast.success('Fatura kaydedildi' + (extras.length ? ' - ' + extras.join(', ') : ''));
            onSaved?.();
        } catch (e) {
            const det = e.response?.data?.detail;
            toast.error(det?.message || det || 'Kayit hatasi');
        } finally { setSaving(false); }
    };
    return (
        <form onSubmit={submit} className="p-5 space-y-3" data-testid="acc-invoice-form-inline">
            <div className="grid grid-cols-2 gap-1.5">
                {[['sales', 'Satis Faturasi'], ['purchase', 'Alis Faturasi'], ['sales_credit', 'Satis Iade'], ['purchase_credit', 'Alis Iade']].map(([t, lbl]) => (
                    <button key={t} type="button" onClick={() => set('type', t)} className={`h-9 rounded-xl text-xs font-medium ${form.type === t ? (t.includes('credit') ? 'bg-rose-500 text-white' : 'bg-[#111827] text-white') : 'bg-[#F3F4F6] text-[#6B7280]'}`}>{lbl}</button>
                ))}
            </div>
            <input value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} placeholder="Fatura No *" required className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm" data-testid="acc-inv-no-input" />
            <div className="grid grid-cols-2 gap-2">
                <div><label className="text-[10px] text-[#6B7280]">Fatura Tarihi</label><input type="date" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)} className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm" /></div>
                <div><label className="text-[10px] text-[#6B7280]">Vade Tarihi</label><input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm" /></div>
            </div>
            <input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Firma Adi" className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm" />
            <div className="grid grid-cols-2 gap-2">
                <input value={form.vat_number} onChange={e => set('vat_number', e.target.value)} placeholder="KDV No" className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm" />
                <input value={form.iban} onChange={e => set('iban', e.target.value)} placeholder="IBAN" className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm" />
            </div>
            <div className="grid grid-cols-3 gap-2">
                <div><label className="text-[10px] text-[#6B7280]">Ara Toplam</label><input type="number" step="0.01" value={form.subtotal} onChange={e => { const v = e.target.value; set('subtotal', v); set('total', (parseFloat(v) || 0) + (parseFloat(form.vat_amount) || 0)); }} className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm" /></div>
                <div><label className="text-[10px] text-[#6B7280]">KDV</label><input type="number" step="0.01" value={form.vat_amount} onChange={e => { const v = e.target.value; set('vat_amount', v); set('total', (parseFloat(form.subtotal) || 0) + (parseFloat(v) || 0)); }} className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm" /></div>
                <div><label className="text-[10px] text-[#6B7280]">Toplam *</label><input type="number" step="0.01" value={form.total} onChange={e => set('total', e.target.value)} required className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm font-bold" /></div>
            </div>
            <input value={form.payment_reference} onChange={e => set('payment_reference', e.target.value)} placeholder="Odeme Referansi (OGM)" className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm" />
            <button type="submit" disabled={saving} className="w-full h-10 bg-[#111827] text-white text-sm font-semibold rounded-xl disabled:opacity-50" data-testid="acc-inv-submit-btn">{saving ? 'Kaydediliyor...' : 'Fatura Ekle'}</button>
        </form>
    );
}

function BatchOcrEditModal({ file, onClose, onSave }) {
    const ext = file.extracted || {};
    const [form, setForm] = useState({
        type: ext.type || 'purchase',
        invoice_number: ext.invoice_number || '',
        invoice_date: ext.invoice_date || new Date().toISOString().slice(0, 10),
        due_date: ext.due_date || '',
        contact_name: ext.contact_name || '',
        vat_number: ext.vat_number || '',
        iban: ext.iban || '',
        currency: ext.currency || 'EUR',
        subtotal: ext.subtotal || 0,
        vat_amount: ext.vat_amount || 0,
        total: ext.total || 0,
        payment_reference: ext.payment_reference || '',
    });
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <form onClick={e => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); onSave({ ...ext, ...form, subtotal: parseFloat(form.subtotal) || 0, vat_amount: parseFloat(form.vat_amount) || 0, total: parseFloat(form.total) || 0 }); }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3" data-testid="acc-ocr-edit-modal">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-[#111]">OCR Sonucunu Duzenle</h3>
                    <button type="button" onClick={onClose} className="text-[#9CA3AF]"><X size={16} /></button>
                </div>
                <div className="flex gap-2">
                    {['sales', 'purchase'].map(t => (
                        <button key={t} type="button" onClick={() => set('type', t)} className={`flex-1 h-8 rounded-lg text-[11px] font-semibold ${form.type === t ? 'bg-[#111827] text-white' : 'bg-[#F3F4F6] text-[#6B7280]'}`}>{t === 'sales' ? 'Satis' : 'Alis'}</button>
                    ))}
                </div>
                <input value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} placeholder="Fatura No *" required className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" data-testid="acc-ocr-edit-no" />
                <div className="grid grid-cols-2 gap-2">
                    <input type="date" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)} className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                    <input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                </div>
                <input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Firma Adi" className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                <div className="grid grid-cols-2 gap-2">
                    <input value={form.vat_number} onChange={e => set('vat_number', e.target.value)} placeholder="KDV No" className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                    <input value={form.iban} onChange={e => set('iban', e.target.value)} placeholder="IBAN" className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <div><label className="text-[9px] text-[#9CA3AF]">Ara Toplam</label><input type="number" step="0.01" value={form.subtotal} onChange={e => { const v = e.target.value; set('subtotal', v); set('total', (parseFloat(v) || 0) + (parseFloat(form.vat_amount) || 0)); }} className="w-full h-8 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" /></div>
                    <div><label className="text-[9px] text-[#9CA3AF]">KDV</label><input type="number" step="0.01" value={form.vat_amount} onChange={e => { const v = e.target.value; set('vat_amount', v); set('total', (parseFloat(form.subtotal) || 0) + (parseFloat(v) || 0)); }} className="w-full h-8 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" /></div>
                    <div><label className="text-[9px] text-[#9CA3AF]">Toplam *</label><input type="number" step="0.01" value={form.total} onChange={e => set('total', e.target.value)} className="w-full h-8 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs font-bold" required /></div>
                </div>
                <input value={form.payment_reference} onChange={e => set('payment_reference', e.target.value)} placeholder="Odeme Referansi" className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                <button type="submit" className="w-full h-9 bg-[#111827] text-white text-xs font-semibold rounded-lg" data-testid="acc-ocr-edit-save-btn">Guncelle</button>
            </form>
        </div>
    );
}
